import React, {FC, useEffect, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import type {FindingSubmitShape, FindingStatus} from '../core/_models'
import RichTextEditor from '../components/RichTextEditor'
import type {Vessel} from '../../Management/core/_models'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type InspectionOption = {
  id: number
  label: string
}

interface AddInspectionFindingModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (data: FindingSubmitShape) => void | Promise<void>
  inspections: InspectionOption[]
  vessels?: Vessel[]
  vesselId?: string
  onVesselChange?: (vesselId: string) => void
  showVesselSelect?: boolean
  inspectionDisabled?: boolean
  inspectionsLoading?: boolean
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const AddInspectionFindingModal: FC<AddInspectionFindingModalProps> = ({
  visible,
  onClose,
  onSubmit,
  inspections,
  vessels = [],
  vesselId = '',
  onVesselChange,
  showVesselSelect = false,
  inspectionDisabled = false,
  inspectionsLoading = false,
}) => {
  const [form, setForm] = useState<FindingSubmitShape>({
    inspectionId: '',
    findingName: '',
    description: '',
    critical: '',
    status: '' as '' | FindingStatus,
    findingType: '' as any,
    remarks: '',
    correctiveAction: '',
    preventiveAction: '',
    attachmentFiles: null,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  // auto-select when only one inspection
  useEffect(() => {
    if (!form.inspectionId && inspections.length === 1) {
      setForm(prev => ({...prev, inspectionId: String(inspections[0].id)}))
    }
  }, [inspections, form.inspectionId])

  // clear inspection selection if list changed and current is not present
  useEffect(() => {
    if (!form.inspectionId) return
    const exists = inspections.some(i => String(i.id) === String(form.inspectionId))
    if (!exists) {
      setForm(prev => ({...prev, inspectionId: ''}))
    }
  }, [inspections, form.inspectionId])

  // reset when visible toggles off→on
  useEffect(() => {
    if (visible) {
      setForm({
        inspectionId: inspections.length === 1 ? String(inspections[0].id) : '',
        findingName: '',
        description: '',
        critical: '',
        status: '' as '' | FindingStatus,
        findingType: '' as any,
        remarks: '',
        correctiveAction: '',
        preventiveAction: '',
        attachmentFiles: null,
      })
      setErrors({})
      setSubmitting(false)
    }
  }, [visible, inspections])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setForm(prev => ({...prev, [name]: value}))
    if (errors[name]) setErrors(prev => ({...prev, [name]: ''}))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {files} = e.target
    const arr: File[] = files && files.length > 0 ? Array.from(files) : []
    setForm(prev => ({
      ...prev,
      attachmentFiles: arr.length > 0 ? arr : null,
    }))
  }

  const handleRemoveSelectedFile = (index: number) => {
    setForm(prev => {
      const current = prev.attachmentFiles || []
      const next = current.filter((_, i) => i !== index)
      return {
        ...prev,
        attachmentFiles: next.length > 0 ? next : null,
      }
    })
  }

  const validate = () => {
    const er: Record<string, string> = {}

    if (!form.inspectionId.trim()) er.inspectionId = 'Inspection is required'
    if (!form.findingName.trim()) er.findingName = 'Finding title is required'

    const descText = (form.description || '').replace(/<[^>]+>/g, '').trim()
    if (!descText) er.description = 'Finding details are required'

    if (!form.status) er.status = 'Status is required'
    if (!form.findingType) er.findingType = 'Finding type is required'
    if (!form.critical) er.critical = 'Criticality is required'
    if (!form.correctiveAction?.trim()) er.correctiveAction = 'Corrective action is required'
    if (!form.preventiveAction?.trim()) er.preventiveAction = 'Preventive action is required'

    setErrors(er)
    return Object.keys(er).length === 0
  }

  const handleSubmit = async () => {
    if (submitting) return
    if (!validate()) return
    try {
      setSubmitting(true)
      await onSubmit(form)
    } finally {
      setSubmitting(false)
    }
  }

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      {/* Custom wide modal window – same style as Defect Add/Edit */}
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '75vw',
          height: '75vh',
          maxWidth: '1400px',
          maxHeight: '900px',
        }}
      >
        {/* Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-6'>Add Inspection Finding</h5>
          <button type='button' className='btn-close m-6' onClick={onClose} />
        </div>

        {/* Body with inner scroll */}
        <div className='flex-grow-1 overflow-auto px-4 py-3 m-6'>
          <div className='row g-4'>
            {showVesselSelect && (
              <div className='col-md-6'>
                <label className={LABEL}>
                  Vessel <span className='text-danger ms-1'>*</span>
                </label>
                <SearchableSelect
                  className='form-select'
                  value={vesselId}
                  onChange={e => onVesselChange?.(e.target.value)}
                >
                  <option value=''>Select vessel</option>
                  {vessels.map(v => (
                    <option key={v.id} value={String(v.id)}>
                      {v.fleet_name || v.name || `Vessel ${v.id}`}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            )}

            {/* Inspection */}
            <div className='col-md-6'>
              <label className={LABEL}>
                Inspection <span className='text-danger ms-1'>*</span>
              </label>
              <SearchableSelect
                name='inspectionId'
                className={`form-select ${errors.inspectionId ? 'is-invalid' : ''}`}
                value={form.inspectionId}
                onChange={handleChange}
                disabled={inspectionDisabled || inspectionsLoading}
              >
                <option value=''>
                  {inspectionDisabled
                    ? 'Select vessel first'
                    : (inspectionsLoading ? 'Loading inspections...' : 'Select inspection')}
                </option>
                {!inspectionDisabled && !inspectionsLoading && inspections.map(i => (
                  <option key={i.id} value={String(i.id)}>
                    {i.label}
                  </option>
                ))}
              </SearchableSelect>
              {errors.inspectionId && (
                <div className='invalid-feedback'>{errors.inspectionId}</div>
              )}
            </div>

            {/* Finding Title / Label */}
            <div className='col-md-6'>
              <label className={LABEL}>
                Finding Title / Label <span className='text-danger ms-1'>*</span>
              </label>
              <input
                name='findingName'
                type='text'
                className={`form-control ${errors.findingName ? 'is-invalid' : ''}`}
                value={form.findingName}
                onChange={handleChange}
                placeholder='e.g. Fire door latch broken'
              />
              {errors.findingName && (
                <div className='invalid-feedback'>{errors.findingName}</div>
              )}
            </div>

            {/* Critical */}
            <div className='col-md-4'>
              <label className={LABEL}>
                Critical <span className='text-danger ms-1'>*</span>
              </label>
              <SearchableSelect
                name='critical'
                className={`form-select ${errors.critical ? 'is-invalid' : ''}`}
                value={form.critical}
                onChange={handleChange}
              >
                <option value=''>Select</option>
                <option value='YES'>Yes</option>
                <option value='NO'>No</option>
              </SearchableSelect>
              {errors.critical && (
                <div className='invalid-feedback'>{errors.critical}</div>
              )}
            </div>

            {/* Status */}
            <div className='col-md-4'>
              <label className={LABEL}>
                Status <span className='text-danger ms-1'>*</span>
              </label>
              <SearchableSelect
                name='status'
                className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                value={form.status}
                onChange={handleChange}
              >
                <option value=''>Select</option>
                <option value='SATISFACTORY'>Satisfactory</option>
                <option value='NON_CRITICAL'>Non-critical</option>
                <option value='CRITICAL'>Critical</option>
                <option value='OBSERVATION'>Observation</option>
                <option value='OPPORTUNITY_FOR_IMPROVEMENT'>
                  Opportunity for improvement
                </option>
              </SearchableSelect>
              {errors.status && (
                <div className='invalid-feedback'>{errors.status}</div>
              )}
            </div>

            {/* Finding Type */}
            <div className='col-md-4'>
              <label className={LABEL}>
                Finding Type <span className='text-danger ms-1'>*</span>
              </label>
              <SearchableSelect
                name='findingType'
                className={`form-select ${errors.findingType ? 'is-invalid' : ''}`}
                value={form.findingType}
                onChange={handleChange}
              >
                <option value=''>Select</option>
                <option value='NON_CONFORMITY_REPORT'>Non conformity report</option>
                <option value='OBSERVATION_NOTE'>Observation note</option>
                <option value='FAILURE_NOTE'>Failure note</option>
                <option value='DEVIATION_NOTE'>Deviation note</option>
              </SearchableSelect>
              {errors.findingType && (
                <div className='invalid-feedback'>{errors.findingType}</div>
              )}
            </div>

            {/* Finding Details (Rich text) */}
            <div className='col-12' style={{paddingBottom: '48px'}}>
              <label className={LABEL}>
                Finding Details <span className='text-danger ms-1'>*</span>
              </label>
              <RichTextEditor
                value={form.description}
                onChange={html =>
                  setForm(prev => ({...prev, description: html}))
                }
                placeholder='Describe the observation / finding in detail'
                invalid={!!errors.description}
                height={220}
              />
              {errors.description && (
                <div className='text-danger mt-1 small'>{errors.description}</div>
              )}
            </div>

            {/* Corrective Action */}
            <div className='col-12 mt-4'>
              <label className={LABEL}>
                Corrective Action <span className='text-danger ms-1'>*</span>
              </label>
              <textarea
                name='correctiveAction'
                className={`form-control ${errors.correctiveAction ? 'is-invalid' : ''}`}
                rows={3}
                value={form.correctiveAction || ''}
                onChange={handleChange}
                placeholder='Describe the corrective action taken'
              />
              {errors.correctiveAction && (
                <div className='invalid-feedback'>{errors.correctiveAction}</div>
              )}
            </div>

            {/* Preventive Action */}
            <div className='col-12 mt-4'>
              <label className={LABEL}>
                Preventive Action <span className='text-danger ms-1'>*</span>
              </label>
              <textarea
                name='preventiveAction'
                className={`form-control ${errors.preventiveAction ? 'is-invalid' : ''}`}
                rows={3}
                value={form.preventiveAction || ''}
                onChange={handleChange}
                placeholder='Describe the preventive action to avoid recurrence'
              />
              {errors.preventiveAction && (
                <div className='invalid-feedback'>{errors.preventiveAction}</div>
              )}
            </div>

            {/* Remarks */}
            <div className='col-12 mt-4'>
              <label className={LABEL}>Remarks</label>
              <textarea
                name='remarks'
                className='form-control'
                rows={3}
                value={form.remarks || ''}
                onChange={handleChange}
              />
            </div>

            {/* Attachments */}
            <div className='col-12'>
              <label className={LABEL}>Attachments (optional)</label>
              <input
                type='file'
                multiple
                className='form-control'
                onChange={handleFileChange}
              />
              {!!form.attachmentFiles?.length && (
                <ul className='mt-2 mb-0 list-unstyled'>
                  {form.attachmentFiles.map((f, idx) => (
                    <li
                      key={idx}
                      className='d-flex align-items-center justify-content-between'
                    >
                      <span className='text-muted small text-truncate me-2'>
                        {f.name}
                      </span>
                      <div className='btn-group'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary'
                          onClick={() => {
                            const url = URL.createObjectURL(f)
                            window.open(url, '_blank')
                            setTimeout(() => URL.revokeObjectURL(url), 60_000)
                          }}
                        >
                          View
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger'
                          onClick={() => handleRemoveSelectedFile(idx)}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
          <button
            type='button'
            className='btn btn-light btn-sm'
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn btn_primary btn-sm'
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save Finding'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddInspectionFindingModal
